import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useAdmin } from '../context/AdminContext';

type Comment = {
  _id: string;
  plantId: string;
  name: string;
  ailment?: string;
  experience: string;
  preparationMethod: string;
  dosage?: string;
  sideEffects?: string;
  createdAt: string;
};

type UpdateRequest = {
  _id: string;
  plantId: string;
  plantName?: string;
  name: string;
  email: string;
  whatNeedsUpdating: string;
  suggestedChange: string;
  reference?: string;
  additionalInfo?: string;
  createdAt: string;
};

export default function AdminDashboard() {
  const { isAdmin, adminPin, logout } = useAdmin();
  const [pendingComments, setPendingComments] = useState<Comment[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<UpdateRequest[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      if (!isAdmin || !adminPin) return;
      setLoading(true);
      try {
        const [commentsRes, updatesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/admin/comments/pending`, {
            headers: { 'x-admin-pin': adminPin }
          }),
          fetch(`${API_BASE_URL}/api/admin/updates/pending`, {
            headers: { 'x-admin-pin': adminPin }
          })
        ]);
        if (commentsRes.ok) {
          const data = await commentsRes.json();
          setPendingComments(data.data || []);
        }
        if (updatesRes.ok) {
          const data = await updatesRes.json();
          setPendingUpdates(data.data || []);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [isAdmin, adminPin]);

  const updateComment = async (id: string, status: 'approved' | 'rejected') => {
    if (!adminPin) return;
    const res = await fetch(`${API_BASE_URL}/api/admin/comments/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': adminPin
      },
      body: JSON.stringify({ status })
    });
    if (res.ok) {
      setPendingComments((prev) => prev.filter((c) => c._id !== id));
    }
  };

  const deleteComment = async (id: string) => {
    if (!adminPin) return;
    const res = await fetch(`${API_BASE_URL}/api/admin/comments/${id}`, {
      method: 'DELETE',
      headers: {
        'x-admin-pin': adminPin
      }
    });
    if (res.ok) {
      setPendingComments((prev) => prev.filter((c) => c._id !== id));
    }
  };

  const updateUpdateRequest = async (id: string, status: 'reviewed' | 'rejected') => {
    if (!adminPin) return;
    const res = await fetch(`${API_BASE_URL}/api/admin/updates/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': adminPin
      },
      body: JSON.stringify({ status })
    });
    if (res.ok) {
      setPendingUpdates((prev) => prev.filter((u) => u._id !== id));
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Admin dashboard</h2>
            <p className="text-xs text-gray-500">
              Approve reviews and review suggested changes
            </p>
          </div>
          <button
            type="button"
            onClick={logout}
            className="text-xs font-medium text-red-600 hover:text-red-800"
          >
            Exit admin
          </button>
        </div>
        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x">
          <div className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center justify-between">
              Pending reviews
              <span className="text-xs text-gray-500">
                {pendingComments.length}
              </span>
            </h3>
            {loading && !pendingComments.length && (
              <p className="text-xs text-gray-500">Loading…</p>
            )}
            {!loading && pendingComments.length === 0 && (
              <p className="text-xs text-gray-500">No pending reviews.</p>
            )}
            <div className="space-y-3">
              {pendingComments.map((c) => (
                <div key={c._id} className="border rounded-lg p-3 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="font-semibold">{c.name}</span>
                    <span className="text-gray-400">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {c.ailment && (
                    <p className="text-gray-600">
                      <span className="font-medium">Ailment:</span> {c.ailment}
                    </p>
                  )}
                  <p className="text-gray-700 whitespace-pre-line">
                    {c.experience}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Prep:</span> {c.preparationMethod}
                  </p>
                  <div className="mt-2 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => deleteComment(c._id)}
                      className="rounded border border-red-200 px-2 py-1 text-[11px] text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => updateComment(c._id, 'rejected')}
                      className="rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => updateComment(c._id, 'approved')}
                      className="rounded bg-green-600 px-2 py-1 text-[11px] text-white hover:bg-green-700"
                    >
                      Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center justify-between">
              Suggested changes
              <span className="text-xs text-gray-500">
                {pendingUpdates.length}
              </span>
            </h3>
            {loading && !pendingUpdates.length && (
              <p className="text-xs text-gray-500">Loading…</p>
            )}
            {!loading && pendingUpdates.length === 0 && (
              <p className="text-xs text-gray-500">No pending update requests.</p>
            )}
            <div className="space-y-3">
              {pendingUpdates.map((u) => (
                <div key={u._id} className="border rounded-lg p-3 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="font-semibold">
                      {u.plantName || u.plantId}
                    </span>
                    <span className="text-gray-400">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-600">
                    <span className="font-medium">From:</span> {u.name} ({u.email})
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium">What needs updating:</span>{' '}
                    {u.whatNeedsUpdating}
                  </p>
                  <p className="text-gray-700 whitespace-pre-line">
                    <span className="font-medium">Suggested change:</span>{' '}
                    {u.suggestedChange}
                  </p>
                  {u.reference && (
                    <p className="text-gray-600">
                      <span className="font-medium">Reference:</span> {u.reference}
                    </p>
                  )}
                  {u.additionalInfo && (
                    <p className="text-gray-600 whitespace-pre-line">
                      <span className="font-medium">Additional info:</span>{' '}
                      {u.additionalInfo}
                    </p>
                  )}
                  <div className="mt-2 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => updateUpdateRequest(u._id, 'rejected')}
                      className="rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => updateUpdateRequest(u._id, 'reviewed')}
                      className="rounded bg-green-600 px-2 py-1 text-[11px] text-white hover:bg-green-700"
                    >
                      Mark reviewed
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

